import { useCallback, useEffect, useRef } from 'react';

export const useResizeObserver = (enabled = true) => {
	const scrollerOuterRef = useRef<HTMLDivElement>(null);
	const scrollerInnerRef = useRef<HTMLDivElement>(null);
	const resizeObserverRef = useRef<ResizeObserver>();
	const isPinnedToBottomRef = useRef(true);

	const bottomThreshold = 32;

	const updatePinnedToBottom = useCallback(() => {
		const scrollerOuter = scrollerOuterRef.current;

		if (!scrollerOuter) {
			return;
		}

		const distanceFromBottom = scrollerOuter.scrollHeight - scrollerOuter.scrollTop - scrollerOuter.clientHeight;

		isPinnedToBottomRef.current = distanceFromBottom <= bottomThreshold;
	}, []);

	const scrollToBottomOfChat = useCallback((behavior: ScrollBehavior = 'auto') => {
		const scrollerOuter = scrollerOuterRef.current;

		if (!scrollerOuter) {
			return;
		}

		scrollerOuter.scrollTo({
			top: scrollerOuter.scrollHeight,
			behavior,
		});
	}, []);

	const destroyResizeObserver = useCallback(() => {
		if (!resizeObserverRef.current) {
			return;
		}

		resizeObserverRef.current.disconnect();
		resizeObserverRef.current = undefined;
	}, []);

	const createNewResizeObserver = useCallback(() => {
		destroyResizeObserver();

		if (!enabled || !scrollerInnerRef.current) {
			return;
		}

		resizeObserverRef.current = new ResizeObserver(() => {
			if (!isPinnedToBottomRef.current) {
				return;
			}

			scrollToBottomOfChat('auto');
		});

		resizeObserverRef.current.observe(scrollerInnerRef.current);
	}, [destroyResizeObserver, enabled, scrollToBottomOfChat]);

	useEffect(() => {
		const scrollerOuter = scrollerOuterRef.current;

		if (!scrollerOuter) {
			return;
		}

		scrollerOuter.addEventListener('scroll', updatePinnedToBottom, {
			passive: true,
		});

		updatePinnedToBottom();

		return () => {
			scrollerOuter.removeEventListener('scroll', updatePinnedToBottom);
		};
	}, [updatePinnedToBottom]);

	useEffect(() => {
		if (enabled && isPinnedToBottomRef.current) {
			scrollToBottomOfChat('auto');
		}
	}, [enabled, scrollToBottomOfChat]);

	useEffect(() => {
		createNewResizeObserver();

		return () => {
			destroyResizeObserver();
		};
	}, [createNewResizeObserver, destroyResizeObserver]);

	return {
		scrollerOuterRef,
		scrollerInnerRef,
	};
};
